import React from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import SEOHead from "../../components/SEOHead";
import { Bot, CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";

export default function RoboticInspection() {
  return (
    <div className="min-h-screen bg-gray-950">
      <SEOHead
        title="Robotic Inspection Systems - Alkane Engineering Limited"
        description="Advanced robotic crawler pipe inspection systems for internal visual and ultrasonic (UT) inspection. Custom solutions for confined space entry inspections and complex industrial structures."
        keywords="robotic inspection, pipe inspection systems, ultrasonic inspection, crawler robots, confined space inspection, VERTI-ROBOT, SAIR, Magneto inspection"
        canonical="https://alkanesengineering.com/services/robotic-inspection"
      />

      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-gray-900 to-gray-800 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Bot className="w-8 h-8 text-primary" />
            <h1 className="text-4xl sm:text-5xl font-bold text-white">
              Robotic Inspection Systems
            </h1>
          </div>
          <p className="text-gray-300 text-lg max-w-3xl mb-8">
            Advanced robotic crawler technology for comprehensive internal inspection of pipes, vessels, and complex industrial structures.
          </p>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              <ArrowLeft size={20} />
              Back to Services
            </Link>
          </div>
        </div>
      </section>

      {/* Pipe Inspection Systems Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8">
            Robotic Crawler Pipe Inspection System
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gray-900 p-8 rounded-lg border border-gray-800">
              <h3 className="text-xl font-semibold text-white mb-6">
                System Capabilities
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span className="text-gray-300">
                    Travel up to 20m through 24" header pipes
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span className="text-gray-300">
                    Deploy camera inspection into 12 adjacent 6" exchanger tubes
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span className="text-gray-300">
                    Custom solutions for internal visual inspection
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span className="text-gray-300">
                    Ultrasonic (UT) inspection capabilities
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span className="text-gray-300">
                    Spectrum SP90 Pan, Tilt and Zoom (PTZ) integration
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span className="text-gray-300">
                    Extended UT cable support up to 30m
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-900 p-8 rounded-lg border border-gray-800">
              <h3 className="text-xl font-semibold text-white mb-6">
                PowerTrain Technology
              </h3>
              <p className="text-gray-300 mb-4">
                The PowerTrain robotic crawler represents the future of pipe inspection technology. Its compact design enables navigation through:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span className="text-gray-300">Tight bends and curves</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span className="text-gray-300">Vertical pipe segments</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span className="text-gray-300">Complex pipeline routes</span>
                </li>
              </ul>
              <p className="text-gray-400 text-sm">
                Provides reliable asset inspection data collection for maintenance and compliance requirements.
              </p>
            </div>
          </div>

          {/* Image Grid - First Section */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-8">
              Inspection System Platforms
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {/* VERTI-ROBOT */}
              <div className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-primary/50 transition-colors">
                <div className="aspect-square bg-gray-800 flex items-center justify-center">
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets%2F8d83d7eadecb4f44a79c56aae0b6143a%2F0876a02d997e48d7bbef02335e46b109?format=webp&width=400&height=400"
                    alt="VERTI-ROBOT"
                    className="w-full h-full object-contain p-4"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-white mb-3">
                    VERTI-ROBOT
                  </h4>
                  <p className="text-gray-400 text-sm">
                    Suitable for pipelines 150mm to 300mm diameter. Travels up to 200m with scalable design.
                  </p>
                </div>
              </div>

              {/* POWERTRAIN PIPE CRAWLER */}
              <div className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-primary/50 transition-colors">
                <div className="aspect-square bg-gray-800 flex items-center justify-center">
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets%2F8d83d7eadecb4f44a79c56aae0b6143a%2F3007a342b7e34a22b77f0dfba883d03a?format=webp&width=400&height=400"
                    alt="POWERTRAIN PIPE CRAWLER"
                    className="w-full h-full object-contain p-4"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-white mb-3">
                    PowerTrain Crawler
                  </h4>
                  <p className="text-gray-400 text-sm">
                    Compact design navigates tight bends and vertical segments for reliable inspection data.
                  </p>
                </div>
              </div>

              {/* ROTOMAG WITH UT MODULE */}
              <div className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-primary/50 transition-colors">
                <div className="aspect-square bg-gray-800 flex items-center justify-center">
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets%2F8d83d7eadecb4f44a79c56aae0b6143a%2F6c83ba5a2e924accbd899aa9745428d7?format=webp&width=400&height=400"
                    alt="ROTOMAG WITH UT MODULE"
                    className="w-full h-full object-contain p-4"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-white mb-3">
                    RotoMag with UT Module
                  </h4>
                  <p className="text-gray-400 text-sm">
                    Combined visual and ultrasonic inspection for comprehensive asset assessment.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Systems Section */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-8">
              Specialized Inspection Solutions
            </h3>

            {/* WHRU Custom Robotic Crawler */}
            <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden mb-8">
              <div className="md:flex">
                <div className="md:w-1/3 bg-gray-800 flex items-center justify-center min-h-80">
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets%2F8d83d7eadecb4f44a79c56aae0b6143a%2F5a672ee890e1451eb215196624705890?format=webp&width=400&height=400"
                    alt="WHRU Custom Robotic Crawler"
                    className="w-full h-full object-contain p-4"
                  />
                </div>
                <div className="md:w-2/3 p-8">
                  <h4 className="text-2xl font-semibold text-white mb-4">
                    Waste Heat Recovery Unit (WHRU) Custom Robotic Crawler
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Custom-engineered solution for internal inspection of waste heat recovery units with advanced thermal capabilities.
                  </p>
                </div>
              </div>
            </div>

            {/* Grid for remaining systems */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* MAGNETO INSPECTION ROBOT */}
              <div className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-primary/50 transition-colors">
                <div className="aspect-square bg-gray-800 flex items-center justify-center">
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets%2F8d83d7eadecb4f44a79c56aae0b6143a%2F39dfa1de77ab4360adf806e35c92c593?format=webp&width=400&height=400"
                    alt="MAGNETO INSPECTION ROBOT"
                    className="w-full h-full object-contain p-4"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-white mb-3">
                    Magneto Inspection Robot
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Leading-edge design with electromagnetic feet and cutting-edge AI technology.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-gray-400 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Climbs and inspects vertical structures</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-400 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Handles complex industrial environments</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-400 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Overcomes confined space challenges</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* SAIR WIRELESS UT CRAWLER */}
              <div className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-primary/50 transition-colors">
                <div className="aspect-square bg-gray-800 flex items-center justify-center">
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets%2F8d83d7eadecb4f44a79c56aae0b6143a%2F7261dd0e4c7d40849a84835ab1ba987e?format=webp&width=400&height=400"
                    alt="SAIR WIRELESS UT CRAWLER"
                    className="w-full h-full object-contain p-4"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-white mb-3">
                    SAIR Wireless UT Crawler
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Advanced wireless magnetic crawler with patented design for superior maneuverability.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-gray-400 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Ultrasonic and visual inspection</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-400 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Works on tanks, vessels, pipes, elbows</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-400 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>8" to flat plate diameter range</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Why Choose Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-8 rounded-lg border border-gray-700 mb-12">
            <h3 className="text-2xl font-bold text-white mb-6">
              Advanced Inspection Technology
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-semibold text-primary mb-2">Precision</h4>
                <p className="text-gray-400 text-sm">
                  High-resolution cameras and ultrasonic sensors for accurate asset assessment.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-primary mb-2">Safety</h4>
                <p className="text-gray-400 text-sm">
                  Eliminates need for dangerous confined space entry by personnel.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-primary mb-2">Efficiency</h4>
                <p className="text-gray-400 text-sm">
                  Complete inspections in minimal time, reducing downtime and costs.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Need Robotic Inspection Services?
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Our advanced robotic inspection systems provide comprehensive asset assessment for pipes, vessels, and complex industrial structures.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-primary/40 transition-all"
            >
              Request Inspection Services
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="flex flex-col sm:flex-row gap-4 mt-12 justify-between">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              <ArrowLeft size={20} />
              Back to Services
            </Link>
            <Link
              to="/services/engineering"
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              Next Service
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
